import React, { useCallback, useContext, useMemo, useState } from 'react';
import Layout from '../../components/layout/main';
import { Fade, Grid, Button, Chip, Stack } from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getDataFull } from '../../services/ajax';
import { AuthContext } from '../../context/auth';
import MaterialTable, { DataType } from '../../components/material-table';
import AddCircleOutlineRoundedIcon from '@mui/icons-material/AddCircleOutlineRounded';
import VisibilityRoundedIcon from '@mui/icons-material/VisibilityRounded';
import AssignmentIcon from '@mui/icons-material/Assignment';
import { useSnackbar } from 'notistack';
import { blue, green } from '@mui/material/colors';
import ListadoOperativoBanner from '../../components/listados/ListadoOperativoBanner';
import { ESTADO_LABELS } from '../../components/orden/OrdenTimeline';
import ProcessGuide from '../../components/orden/ProcessGuide';
import CountdownChip from '../../components/orden/CountdownChip';

const CHIP_DEFS = [
    { key: 'todos', label: 'Todos', color: 'primary' },
    { key: 'mis_pedidos', label: 'Mis pedidos', color: 'info' },
    { key: 'para_responder', label: 'Para responder', color: 'warning' },
    { key: 'cerrar_turno', label: 'Cerrar turno', color: 'error' },
    { key: 'insumos', label: 'Insumos', color: 'warning', adminOnly: true },
    { key: 'destrabar', label: 'Destrabar', color: 'error', adminOnly: true },
    { key: 'con_turno', label: 'Con turno', color: 'success' },
    { key: 'cerradas', label: 'Cerradas', color: 'default' },
    { key: 'canceladas', label: 'Canceladas', color: 'default', adminOnly: true },
];

function Page() {
    const { usuario } = useContext(AuthContext);
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const { enqueueSnackbar } = useSnackbar();

    const isAdmin = !!(usuario?.es_admin || usuario?.nivel_usuario === 1);
    const puedeCargar = !!(usuario?.puede_cargar_ordenes || isAdmin);

    const initialChip = searchParams.get('chip') || 'todos';
    const [selectedChip, setSelectedChip] = useState(initialChip);
    const [counts, setCounts] = useState({});
    const [rowsTableMain, setRowsTableMain] = useState(null);
    const [tableMainNeedUpdate, setTableMainNeedUpdate] = useState(Date.now());

    const visibleChips = useMemo(
        () => CHIP_DEFS.filter((c) => !c.adminOnly || isAdmin),
        [isAdmin]
    );

    const chipItems = useMemo(
        () => visibleChips.map((c) => ({
            ...c,
            count: counts[c.key] != null ? counts[c.key] : 0,
        })),
        [visibleChips, counts]
    );

    const onChipChange = useCallback((key) => {
        setSelectedChip(key);
        setSearchParams(key === 'todos' ? {} : { chip: key });
        setTableMainNeedUpdate(Date.now());
    }, [setSearchParams]);

    const onNuevo = useCallback(() => {
        navigate('/orden-practica/nueva');
    }, [navigate]);

    const onVer = useCallback((row) => {
        navigate(`/orden-practica/${row.id}`);
    }, [navigate]);

    const tableMain = useMemo(() => ({
        title: 'Listado',
        idName: 'id',
        defaultOrder: 'desc',
        headers: [
            { id: 'codigo', label: 'Código', dataType: DataType.TEXT, sortable: true },
            { id: 'paciente_nombre', label: 'Paciente', dataType: DataType.TEXT, sortable: false },
            { id: 'practica_nombre', label: 'Práctica', dataType: DataType.TEXT, sortable: false },
            {
                id: 'estado',
                label: 'Estado',
                dataType: DataType.TEXT,
                sortable: true,
                customRender: (row) => (
                    <Chip
                        size="small"
                        label={ESTADO_LABELS[row.estado] || row.estado}
                        color={
                            row.estado === 'cerrada' ? 'success'
                                : row.estado === 'cancelada' ? 'default'
                                    : row.estado === 'asignada' || row.estado === 'pendiente_manual' ? 'warning'
                                        : 'primary'
                        }
                        variant="outlined"
                    />
                ),
            },
            { id: 'establecimiento_nombre', label: 'Establecimiento', dataType: DataType.TEXT, sortable: false },
            {
                id: 'plazos',
                label: 'Plazos',
                dataType: DataType.TEXT,
                sortable: false,
                customRender: (row) => (
                    <Stack direction="row" spacing={0.5} useFlexGap sx={{ flexWrap: 'wrap' }}>
                        {row.tiempos_restantes?.respuesta != null ? (
                            <CountdownChip
                                label="Respuesta"
                                remainingSeconds={row.tiempos_restantes.respuesta}
                                endsAt={row.respuesta_vence_at}
                                totalSeconds={(row.plazo_respuesta_minutos || 0) * 60}
                                live={false}
                            />
                        ) : null}
                        {row.tiempos_restantes?.insumo != null ? (
                            <CountdownChip
                                label="Insumo"
                                remainingSeconds={row.tiempos_restantes.insumo}
                                endsAt={row.insumo_vence_at}
                                totalSeconds={(row.plazo_compra_horas || 0) * 3600}
                                live={false}
                            />
                        ) : null}
                        {row.tiempos_restantes?.turno_pendiente_cierre ? (
                            <Chip size="small" color="error" label="Cierre pendiente" />
                        ) : null}
                    </Stack>
                ),
            },
        ],
        loadData: async (orderBy, order, page, rowsPerPage, searchText, _selectedFilter, pageUpdate, totalUpdate, filterListUpdate) => {
            setRowsTableMain(null);
            const apiPage = page + 1;
            const params = new URLSearchParams();
            params.append('per_page', String(rowsPerPage));
            params.append('page', String(apiPage));
            if (orderBy) params.append('order_by', orderBy);
            if (order) params.append('order', order);
            if (searchText) params.append('search_text', searchText);
            if (selectedChip && selectedChip !== 'todos') params.append('chip', selectedChip);

            const response = await getDataFull(`ordenes-practicas?${params.toString()}`, usuario.token);
            if (response.error === '') {
                pageUpdate(response.meta?.page ? response.meta.page - 1 : page);
                totalUpdate(response.meta?.total || 0);
                if (filterListUpdate) filterListUpdate([]);
                if (response.counts) setCounts(response.counts);
                setRowsTableMain(response.data || []);
            } else {
                enqueueSnackbar(response.error, { variant: 'error' });
            }
        },
        moreMenuItems: puedeCargar ? [
            {
                label: 'Nueva orden',
                secondary: 'Registrar pedido de práctica',
                icon: <AddCircleOutlineRoundedIcon sx={{ color: green[600] }} />,
                itemColor: green[600],
                action: () => onNuevo(),
            },
        ] : [],
        rowActions: (row) => [
            {
                label: 'Ver detalle',
                secondary: 'Abrir la orden y sus acciones',
                icon: <VisibilityRoundedIcon sx={{ color: blue[600] }} />,
                itemColor: blue[600],
                action: () => onVer(row),
            },
        ],
    }), [usuario.token, selectedChip, puedeCargar, onNuevo, onVer, enqueueSnackbar]);

    // Sync chip from URL (deep-link desde notificaciones)
    React.useEffect(() => {
        const chip = searchParams.get('chip') || 'todos';
        if (chip !== selectedChip) {
            setSelectedChip(chip);
            setTableMainNeedUpdate(Date.now());
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchParams]);

    return (
        <Layout nombrePagina="Órdenes de práctica">
            <Fade in timeout={900}>
                <Grid size={12}>
                    <ListadoOperativoBanner
                        title="Órdenes de práctica"
                        description="Consulta y gestiona pedidos según su estado. Use los filtros para priorizar respuestas, cierres e intervenciones."
                        icon={AssignmentIcon}
                        accentColor="primary"
                        headerAction={puedeCargar ? (
                            <Button
                                startIcon={<AddCircleOutlineRoundedIcon />}
                                onClick={onNuevo}
                            >
                                Nueva orden
                            </Button>
                        ) : null}
                        chips={chipItems}
                        selectedChip={selectedChip}
                        onChipChange={onChipChange}
                    />

                    <ProcessGuide
                        title="Guía rápida"
                        steps={[
                            'Seleccione un filtro superior para acotar el listado.',
                            'Abra una orden para responder, cerrar, registrar insumos o cancelar según el rol.',
                            'Los plazos críticos se destacan cuando queda menos del 25 % del tiempo.',
                        ]}
                    />

                    <MaterialTable
                        title={tableMain.title}
                        loadData={tableMain.loadData}
                        headers={tableMain.headers}
                        data={rowsTableMain}
                        moreMenuItems={tableMain.moreMenuItems}
                        selectable={false}
                        rowActions={tableMain.rowActions}
                        idName={tableMain.idName}
                        defaultOrder={tableMain.defaultOrder}
                        defaultRowsPerPage={10}
                        needUpdate={tableMainNeedUpdate}
                        hideFilters
                        onRowClick={(row) => onVer(row)}
                    />
                </Grid>
            </Fade>
        </Layout>
    );
}

export default Page;
